import React from 'react'
import { PropTypes } from 'prop-types'
import { graphql } from '@apollo/client/react/hoc'
import { flowRight, isFunction } from 'lodash'
import { GA_EVENT, gtm } from 'lib/gtm'

import ActionLogic from 'components/Util/ActionLogic'

import addEvent from 'graphql/event/addEvent.gql'
import updateEvent from 'graphql/event/updateEvent.gql'
import deleteEvent from 'graphql/event/deleteEvent.gql'
import confirmEvent from 'graphql/event/confirmEvent.gql'
import getTasks from 'graphql/task/getTasks.gql'
import createLatestInspectionEventFromTrafiData from 'graphql/event/createLatestInspectionEventFromTrafiData.gql'
import { updateEventCacheMergeAndModify } from 'lib/utils/store/event'

export const EventLogicContext = React.createContext()

class EventLogic extends React.Component {
    addEvent = async (actionLogic, vehicleId, input, isReminder) => {
        const options = {
            variables: {
                vehicleId: vehicleId,
                input,
            },
            update: (cache, { data }) => {
                updateEventCacheMergeAndModify(cache, data.addEvent)
            },
            refetchQueries: ['getSumOfEvents', 'getTimelineTasks'],
        }

        return actionLogic.callMutation(this.props.addEvent, options)
            .then((result) => {
                const action = `Add ${isReminder ? 'reminder' : 'event'}`
                gtm.pushGaEvent(GA_EVENT.EVENT, {
                    action,
                    label: input ? input.type : null,
                })

                return Promise.resolve(result)
            })
    }

    updateEvent = async (actionLogic, eventId, input, isReminder) => {
        const options = {
            variables: {
                eventId,
                input,
            },
            refetchQueries: ['getSumOfEvents'],
        }

        return actionLogic.callMutation(this.props.updateEvent, options)
            .then((result) => {
                if (input.confirmEvent) {
                    gtm.pushGaEvent(GA_EVENT.EVENT, {
                        action: 'Confirm',
                        label: input ? input.type : null,
                    })
                } else {
                    const action = `Update ${isReminder ? 'reminder' : 'event'}`
                    gtm.pushGaEvent(GA_EVENT.EVENT, {
                        action,
                        label: input ? input.type : null,
                    })
                }

                return Promise.resolve(result)
            })
    }

    /**
     * Delete event
     * @param actionLogic
     * @param eventId
     * @param vehicleId Provide vehicleId to refetch vehicle events right after deletion.
     * @param eventType Event type for GA event logging
     * @param isReminder For GA event logging
     * @returns {Promise.<*>}
     */
    deleteEvent = async (actionLogic, eventId, eventType = null, isReminder = null) => {
        const options = {
            variables: {
                id: eventId,
            },
            update: (cache, { data }) => {
                cache.modify({
                    fields: {
                        events: (existing) => {
                            const deletedEventId = data.deleteEvent

                            let cursor = existing.cursor

                            // move cursor back if cursor event is being deleted
                            if (existing.cursor === deletedEventId) {
                                const cursorIndex = existing.events.findIndex(event => event.__ref === `Event:${existing.cursor}`)
                                const newCursorIndex = Math.max(0, cursorIndex - 1)

                                const newCursorEvent = existing.events[newCursorIndex]
                                cursor = newCursorEvent.__ref.split(':')[1] // `Event:123456`
                            }

                            return {
                                ...existing,
                                cursor,
                                events: existing.events.filter((event) => event.__ref !== `Event:${data.deleteEvent}`)
                            }
                        }
                    }
                })
            },
            refetchQueries: ['getSumOfEvents', 'getTimelineTasks'],
        }

        return actionLogic.callMutation(this.props.deleteEvent, options)
            .then((result) => {
                const action = `Delete ${isReminder ? 'reminder' : 'event'}`
                gtm.pushGaEvent(GA_EVENT.EVENT, {
                    action,
                    label: eventType,
                })

                return Promise.resolve(result)
            })
    }

    confirmEvent = async (actionLogic, eventId, eventType) => {
        const options = {
            variables: {
                id: eventId,
            },
        }

        return actionLogic.callMutation(this.props.confirmEvent, options)
            .then((result) => {
                gtm.pushGaEvent(GA_EVENT.EVENT, {
                    action: 'Confirm reminder',
                    label: eventType,
                })

                return Promise.resolve(result)
            })
    }

    createLatestInspectionEventFromTrafiData = async (actionLogic, vehicleId) => {
        return actionLogic.callMutation(this.props.createLatestInspectionEventFromTrafiData, {
            variables: {
                vehicleId,
            },
            update: (cache, { data }) => {
                updateEventCacheMergeAndModify(cache, data.createLatestInspectionEventFromTrafiData)
            },
            refetchQueries: [{
                query: getTasks,
                variables: {
                    vehicleId: vehicleId,
                }
            }],
            awaitRefetchQueries: true,
        })
            .then((result) => {
                if (result.data.createLatestInspectionEventFromTrafiData) {
                    gtm.pushGaEvent(GA_EVENT.CREATE_LATEST_INSPECTION_FROM_TRAFI_DATA)
                }

                return Promise.resolve(result)
            })
    }

    render () {
        const { children } = this.props

        return (
            <ActionLogic>
                {(actionLogic) => {
                    const props = {
                        ...actionLogic,
                        addEvent: (...args) => this.addEvent(actionLogic, ...args),
                        updateEvent: (...args) => this.updateEvent(actionLogic, ...args),
                        deleteEvent: (...args) => this.deleteEvent(actionLogic, ...args),
                        confirmEvent: (...args) => this.confirmEvent(actionLogic, ...args),
                        createLatestInspectionEventFromTrafiData: (...args) => this.createLatestInspectionEventFromTrafiData(actionLogic, ...args),
                    }
                    return (
                        <EventLogicContext.Provider value={props}>
                            {isFunction(children) ? children(props) : children}
                        </EventLogicContext.Provider>
                    )
                }}
            </ActionLogic>
        )
    }
}

EventLogic.propTypes = {
    children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
}

export default flowRight(
    graphql(addEvent, {
        name: 'addEvent',
    }),
    graphql(updateEvent, {
        name: 'updateEvent',
    }),
    graphql(deleteEvent, {
        name: 'deleteEvent',
    }),
    graphql(confirmEvent, {
        name: 'confirmEvent',
    }),
    graphql(createLatestInspectionEventFromTrafiData, {
        name: 'createLatestInspectionEventFromTrafiData'
    }),
)(EventLogic)
